// Tracks in-flight event-driven animations (callouts, future movement
// reveals, reaction prompts, etc.) so consumers can defer follow-on UI
// until the animation queue is idle.
//
// Subsystems that produce animations call `begin(id)` when they start
// and `end(id)` when they finish (or hit a safety timeout). The tracker
// notifies subscribers ONLY on transitions between active and idle —
// it never fires per begin/end while already in a state, so the cost
// of subscribing is bounded regardless of how many concurrent
// animations are running.
//
// Lives in its own singleton — not on mapState — because it represents
// transient render-pipeline progress, not game state.
class EventAnimationState {
  constructor() {
    /** @type {Set<string|number>} */
    this._activeIds = new Set();
    /** @type {Set<() => void>} */
    this._listeners = new Set();
  }

  /** @param {() => void} fn */
  subscribe(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  /** @param {string|number} id */
  begin(id) {
    if (this._activeIds.has(id)) return;
    const wasIdle = this._activeIds.size === 0;
    this._activeIds.add(id);
    if (wasIdle) this._notify();
  }

  /** @param {string|number} id */
  end(id) {
    if (!this._activeIds.has(id)) return;
    this._activeIds.delete(id);
    if (this._activeIds.size === 0) this._notify();
  }

  isIdle() {
    return this._activeIds.size === 0;
  }

  _notify() {
    for (const fn of this._listeners) {
      try {
        fn();
      } catch (err) {
        console.error('eventAnimationState listener error', err);
      }
    }
  }
}

export const eventAnimationState = new EventAnimationState();
